import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Check, Clock, Circle, Trash, PencilSimple,
  WhatsappLogo, CalendarBlank, Tag, ListBullets,
  Kanban, CalendarDots, CaretLeft, CaretRight,
  DotsSixVertical, ArrowsClockwise, CurrencyDollar, X, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'trabalho', label: 'Trabalho', color: 'bg-blue-500' },
  { value: 'pessoal', label: 'Pessoal', color: 'bg-purple-500' },
  { value: 'financeiro', label: 'Financeiro', color: 'bg-green-500' },
  { value: 'saúde', label: 'Saúde', color: 'bg-red-500' },
  { value: 'estudos', label: 'Estudos', color: 'bg-yellow-500' },
  { value: 'compras', label: 'Compras', color: 'bg-pink-500' },
  { value: 'família', label: 'Família', color: 'bg-orange-500' },
  { value: 'lazer', label: 'Lazer', color: 'bg-cyan-500' },
  { value: 'urgente', label: 'Urgente', color: 'bg-red-600' },
  { value: 'geral', label: 'Geral', color: 'bg-gray-500' },
];

const COLUMNS = [
  { id: 'pending', label: 'Pendente', icon: Circle, color: 'text-yellow-500', borderColor: 'border-yellow-400' },
  { id: 'in_progress', label: 'Em Andamento', icon: Clock, color: 'text-blue-500', borderColor: 'border-blue-400' },
  { id: 'completed', label: 'Concluída', icon: Check, color: 'text-green-500', borderColor: 'border-green-400' },
];

const getCategoryConfig = (category) => CATEGORIES.find(c => c.value === category) || CATEGORIES[9];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const isOverdue = (task) => {
  if (!task.due_date || task.status === 'completed') return false;
  return task.due_date < new Date().toISOString().split('T')[0];
};

// ==================== SORTABLE TASK CARD ====================
function SortableTaskCard({ task, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.task_id,
    data: { task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const cat = getCategoryConfig(task.category);
  const overdue = isOverdue(task);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`neo-card-sm mb-3 cursor-grab active:cursor-grabbing group transition-all
        ${overdue ? 'ring-2 ring-red-400/50' : ''}
        ${isDragging ? 'shadow-xl scale-105' : 'hover:translate-y-[-2px]'}
      `}
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className="mt-1 text-[var(--neo-text-light)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          data-testid={`task-drag-handle-${task.task_id}`}
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <h4 className={`text-sm font-semibold leading-tight ${task.status === 'completed' ? 'line-through text-[var(--neo-text-light)]' : 'text-[var(--neo-text)]'}`}>
              {task.title}
            </h4>
            <div className="flex shrink-0 gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => onEdit(task)}
                className="p-1 rounded hover:bg-[var(--neo-bg-dark)] transition-colors"
                data-testid={`task-edit-${task.task_id}`}
              >
                <PencilSimple size={14} className="text-[var(--neo-text-light)]" />
              </button>
              <button
                onClick={() => onDelete(task.task_id)}
                className="p-1 rounded hover:bg-red-100 transition-colors"
                data-testid={`task-delete-${task.task_id}`}
              >
                <Trash size={14} className="text-red-400" />
              </button>
            </div>
          </div>

          {task.description && (
            <p className="text-xs text-[var(--neo-text-light)] mt-1 line-clamp-2">{task.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold text-white ${cat.color}`}>
              {cat.label}
            </span>

            {task.due_date && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${overdue ? 'text-red-500' : 'text-[var(--neo-text-light)]'}`}>
                <CalendarBlank size={10} />
                {formatDate(task.due_date)}
                {task.due_time && ` ${task.due_time}`}
              </span>
            )}

            {overdue && (
              <span className="text-[10px] font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded">Atrasada</span>
            )}

            {task.is_recurring && (
              <ArrowsClockwise size={12} className="text-purple-400" title="Recorrente" />
            )}

            {task.is_financial && (
              <CurrencyDollar size={12} className="text-green-500" title="Financeira" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== OVERLAY CARD (while dragging) ====================
function DragOverlayCard({ task }) {
  if (!task) return null;
  const cat = getCategoryConfig(task.category);

  return (
    <div className="neo-card-sm shadow-2xl scale-105 rotate-2 w-72 border-2 border-[var(--neo-green)]">
      <h4 className="text-sm font-semibold text-[var(--neo-text)]">{task.title}</h4>
      <div className="flex items-center gap-1.5 mt-2">
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold text-white ${cat.color}`}>
          {cat.label}
        </span>
        {task.due_date && (
          <span className="text-[10px] text-[var(--neo-text-light)]">
            <CalendarBlank size={10} className="inline mr-0.5" />
            {formatDate(task.due_date)}
          </span>
        )}
      </div>
    </div>
  );
}

// ==================== KANBAN COLUMN ====================
function KanbanColumn({ column, tasks, onEdit, onDelete }) {
  const taskIds = tasks.map(t => t.task_id);
  
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', status: column.id },
  });

  return (
    <div
      className="flex-1 min-w-[280px] max-w-[400px]"
      data-testid={`kanban-column-${column.id}`}
    >
      <div className={`flex items-center gap-2 mb-4 pb-3 border-b-2 ${column.borderColor}`}>
        <column.icon size={20} weight="bold" className={column.color} />
        <h3 className="font-bold text-[var(--neo-text)]">{column.label}</h3>
        <span className="ml-auto text-sm font-semibold text-[var(--neo-text-light)] bg-[var(--neo-bg-dark)] px-2 py-0.5 rounded-full">
          {tasks.length}
        </span>
      </div>

      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`min-h-[200px] space-y-0 rounded-lg p-1 transition-colors ${isOver ? 'bg-[var(--neo-green)]/10 ring-2 ring-[var(--neo-green)]/30' : ''}`}
        >
          {tasks.length === 0 && (
            <div className="text-center py-8 text-sm text-[var(--neo-text-light)] opacity-60">
              Nenhuma tarefa
            </div>
          )}
          {tasks.map(task => (
            <SortableTaskCard
              key={task.task_id}
              task={task}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

// ==================== CALENDAR VIEW ====================
function CalendarView({ tasks, onEdit, onDelete, onToggleStatus, onDayClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().split('T')[0];

  const monthName = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const getTasksForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(t => t.due_date === dateStr);
  };

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="calendar-prev">
          <CaretLeft size={20} />
        </Button>
        <h3 className="text-lg font-bold capitalize text-[var(--neo-text)]">{monthName}</h3>
        <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="calendar-next">
          <CaretRight size={20} />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
          <div key={d} className="text-center text-xs font-semibold text-[var(--neo-text-light)] py-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} className="h-24" />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dayTasks = getTasksForDay(day);
          const isToday = dateStr === today;

          return (
            <button
              key={day}
              onClick={() => onDayClick(dateStr, dayTasks)}
              className={`neo-card-sm !p-2 min-h-[96px] transition-all text-left cursor-pointer hover:shadow-md
                ${isToday ? 'ring-2 ring-[var(--neo-green)]' : ''}
                ${dayTasks.length > 0 ? 'hover:scale-[1.02]' : ''}
              `}
              data-testid={`calendar-day-${dateStr}`}
            >
              <div className={`text-xs font-bold mb-1 ${isToday ? 'text-[var(--neo-green)]' : 'text-[var(--neo-text-light)]'}`}>
                {day}
                {dayTasks.length > 0 && (
                  <span className="ml-1 text-[9px] font-semibold text-[var(--neo-text-light)] bg-[var(--neo-bg-dark)] px-1 rounded-full">{dayTasks.length}</span>
                )}
              </div>
              <div className="space-y-1">
                {dayTasks.slice(0, 3).map(task => {
                  const statusColor = task.status === 'completed' ? 'bg-green-400' : task.status === 'in_progress' ? 'bg-blue-400' : 'bg-yellow-400';
                  return (
                    <div
                      key={task.task_id}
                      className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate text-white font-medium ${statusColor}`}
                      title={task.title}
                      data-testid={`calendar-task-${task.task_id}`}
                    >
                      {task.due_time && <span className="opacity-80">{task.due_time} </span>}
                      {task.title}
                    </div>
                  );
                })}
                {dayTasks.length > 3 && (
                  <div className="text-[10px] text-[var(--neo-text-light)] font-medium text-center">
                    +{dayTasks.length - 3} mais
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ==================== MAIN TASK BOARD ====================
export default function TaskBoard() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, in_progress: 0, completed: 0, today: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeTask, setActiveTask] = useState(null);

  const [formData, setFormData] = useState({
    title: '', description: '', category: 'geral', tags: [], status: 'pending',
    due_date: '', due_time: '', reminder_minutes: 30, whatsapp_number: '',
    is_recurring: false, recurrence_type: null,
    is_financial: false, financial_type: null, financial_amount: null,
  });
  const [tagInput, setTagInput] = useState('');
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState({ date: '', tasks: [] });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/tasks`, { withCredentials: true });
      setTasks(res.data.tasks || []);
    } catch (error) {
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tasks/stats/summary`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const openCreateModal = () => {
    setSelectedTask(null);
    setFormData({
      title: '', description: '', category: 'geral', tags: [], status: 'pending',
      due_date: '', due_time: '', reminder_minutes: 30, whatsapp_number: '',
      is_recurring: false, recurrence_type: null,
      is_financial: false, financial_type: null, financial_amount: null,
    });
    setTagInput('');
    setModalOpen(true);
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title, description: task.description || '', category: task.category,
      tags: task.tags || [], status: task.status, due_date: task.due_date || '',
      due_time: task.due_time || '', reminder_minutes: task.reminder_minutes || 30,
      whatsapp_number: task.whatsapp_number || '',
      is_recurring: task.is_recurring || false, recurrence_type: task.recurrence_type || null,
      is_financial: task.is_financial || false, financial_type: task.financial_type || null,
      financial_amount: task.financial_amount || null,
    });
    setTagInput('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error('Digite um título'); return; }

    try {
      if (selectedTask) {
        await axios.patch(`${BACKEND_URL}/api/tasks/${selectedTask.task_id}`, formData, { withCredentials: true });
        toast.success('Tarefa atualizada!');
      } else {
        await axios.post(`${BACKEND_URL}/api/tasks`, formData, { withCredentials: true });
        toast.success('Tarefa criada!');
      }
      setModalOpen(false);
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao salvar tarefa');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Excluir esta tarefa?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`, { withCredentials: true });
      toast.success('Tarefa excluída!');
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao excluir tarefa');
    }
  };

  const toggleStatus = async (taskId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/tasks/${taskId}/toggle-status`, {}, { withCredentials: true });
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  // ========== DnD handlers ==========
  const handleDragStart = (event) => {
    const task = event.active.data?.current?.task;
    setActiveTask(task || null);
  };

  const handleDragEnd = useCallback(async (event) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const draggedTaskId = active.id;
    const draggedTask = tasks.find(t => t.task_id === draggedTaskId);
    if (!draggedTask) return;

    let targetStatus = null;

    // Check if dropped on a column (droppable)
    const overData = over.data?.current;
    if (overData?.type === 'column') {
      targetStatus = overData.status;
    } else if (overData?.task) {
      // Dropped on another task card - use that task's status
      targetStatus = overData.task.status;
    } else {
      // Check if over.id matches a column id
      const col = COLUMNS.find(c => c.id === over.id);
      if (col) {
        targetStatus = col.id;
      } else {
        // Last resort: find the task by id and get its status
        const overTask = tasks.find(t => t.task_id === over.id);
        if (overTask) targetStatus = overTask.status;
      }
    }

    if (!targetStatus || targetStatus === draggedTask.status) return;

    // Optimistic update
    setTasks(prev => prev.map(t => t.task_id === draggedTaskId ? { ...t, status: targetStatus } : t));

    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${draggedTaskId}`, { status: targetStatus }, { withCredentials: true });
      fetchStats();
    } catch (error) {
      toast.error('Erro ao mover tarefa');
      fetchTasks();
    }
  }, [tasks]);

  const handleDragOver = (event) => {
    // Allow dropping between columns
  };

  const handleDayClick = (dateStr, dayTasks) => {
    setSelectedDay({ date: dateStr, tasks: dayTasks });
    setDayModalOpen(true);
  };

  // ========== Tag helpers ==========
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  // ========== Grouped tasks for Kanban ==========
  const tasksByStatus = {
    pending: tasks.filter(t => t.status === 'pending'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    completed: tasks.filter(t => t.status === 'completed'),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="task-board-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[var(--neo-text-dark)]">Tarefas</h1>
          <p className="text-sm text-[var(--neo-text-light)] mt-1">Gerencie suas tarefas e compromissos</p>
        </div>
        <Button onClick={openCreateModal} className="neo-btn" data-testid="create-task-btn">
          <Plus size={18} className="mr-2" />
          Nova Tarefa
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'text-[var(--neo-text)]' },
          { label: 'Pendentes', value: stats.pending, color: 'text-yellow-500' },
          { label: 'Andamento', value: stats.in_progress, color: 'text-blue-500' },
          { label: 'Concluídas', value: stats.completed, color: 'text-green-500' },
          { label: 'Hoje', value: stats.today, color: 'text-purple-500' },
          { label: 'Atrasadas', value: stats.overdue, color: 'text-red-500' },
        ].map(s => (
          <div key={s.label} className="neo-card-sm !py-3 text-center" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-[10px] font-medium text-[var(--neo-text-light)] uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-1 mb-6 neo-card-sm !p-1 w-fit" data-testid="view-toggle">
        {[
          { id: 'kanban', label: 'Kanban', icon: Kanban },
          { id: 'calendar', label: 'Calendário', icon: CalendarDots },
          { id: 'list', label: 'Lista', icon: ListBullets },
        ].map(v => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all
              ${view === v.id
                ? 'bg-[var(--neo-green)] text-white shadow-md'
                : 'text-[var(--neo-text-light)] hover:bg-[var(--neo-bg-dark)]'
              }
            `}
            data-testid={`view-toggle-${v.id}`}
          >
            <v.icon size={16} />
            <span className="hidden sm:inline">{v.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <LoadingBar text="Carregando tarefas..." />
      ) : view === 'kanban' ? (
        /* ========== KANBAN VIEW ========== */
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
            {COLUMNS.map(col => (
              <KanbanColumn
                key={col.id}
                column={col}
                tasks={tasksByStatus[col.id]}
                onEdit={openEditModal}
                onDelete={handleDelete}
              />
            ))}
          </div>
          <DragOverlay>
            <DragOverlayCard task={activeTask} />
          </DragOverlay>
        </DndContext>
      ) : view === 'calendar' ? (
        /* ========== CALENDAR VIEW ========== */
        <div className="neo-card">
          <CalendarView
            tasks={tasks}
            onEdit={openEditModal}
            onDelete={handleDelete}
            onToggleStatus={toggleStatus}
            onDayClick={handleDayClick}
          />
        </div>
      ) : (
        /* ========== LIST VIEW ========== */
        <div className="space-y-3" data-testid="list-view">
          {tasks.length === 0 ? (
            <div className="neo-card text-center py-12">
              <ListBullets size={48} className="mx-auto text-[var(--neo-text-light)] opacity-30 mb-4" />
              <h3 className="text-lg font-bold mb-2 text-[var(--neo-text)]">Nenhuma tarefa</h3>
              <p className="text-sm text-[var(--neo-text-light)] mb-4">Comece criando sua primeira tarefa!</p>
              <Button onClick={openCreateModal} className="neo-btn">
                <Plus size={18} className="mr-2" /> Nova Tarefa
              </Button>
            </div>
          ) : tasks.map(task => {
            const cat = getCategoryConfig(task.category);
            const overdue = isOverdue(task);
            const statusCol = COLUMNS.find(c => c.id === task.status);

            return (
              <div
                key={task.task_id}
                className={`neo-card-sm flex items-center gap-4 transition-all hover:translate-y-[-1px] ${overdue ? 'ring-2 ring-red-400/40' : ''}`}
                data-testid={`list-task-${task.task_id}`}
              >
                <button
                  onClick={() => toggleStatus(task.task_id)}
                  className={`p-1.5 rounded-full ${task.status === 'completed' ? 'bg-green-100' : task.status === 'in_progress' ? 'bg-blue-100' : 'bg-yellow-100'}`}
                  data-testid={`list-toggle-${task.task_id}`}
                >
                  {statusCol && <statusCol.icon size={18} weight="bold" className={statusCol.color} />}
                </button>

                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-sm ${task.status === 'completed' ? 'line-through text-[var(--neo-text-light)]' : ''}`}>
                    {task.title}
                  </h4>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold text-white ${cat.color}`}>{cat.label}</span>
                    {task.due_date && (
                      <span className={`text-[10px] ${overdue ? 'text-red-500 font-bold' : 'text-[var(--neo-text-light)]'}`}>
                        {formatDate(task.due_date)}{task.due_time && ` ${task.due_time}`}
                      </span>
                    )}
                    {overdue && <span className="text-[10px] font-bold text-red-500">Atrasada</span>}
                  </div>
                </div>

                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEditModal(task)} className="p-1.5 rounded-lg hover:bg-[var(--neo-bg-dark)]" data-testid={`list-edit-${task.task_id}`}>
                    <PencilSimple size={16} className="text-[var(--neo-text-light)]" />
                  </button>
                  <button onClick={() => handleDelete(task.task_id)} className="p-1.5 rounded-lg hover:bg-red-100" data-testid={`list-delete-${task.task_id}`}>
                    <Trash size={16} className="text-red-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========== DAY DETAIL MODAL ========== */}
      <Dialog open={dayModalOpen} onOpenChange={setDayModalOpen}>
        <DialogContent className="sm:max-w-[460px] max-h-[80vh] overflow-y-auto" aria-describedby="day-detail-desc">
          <DialogHeader>
            <DialogTitle>
              Tarefas de {selectedDay.date ? new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
            </DialogTitle>
            <p id="day-detail-desc" className="text-sm text-muted-foreground sr-only">Lista de tarefas para o dia selecionado</p>
          </DialogHeader>
          <div className="space-y-2">
            {selectedDay.tasks.length === 0 ? (
              <div className="text-center py-8">
                <CalendarBlank size={40} className="mx-auto text-[var(--neo-text-light)] opacity-30 mb-3" />
                <p className="text-sm text-[var(--neo-text-light)]">Nenhuma tarefa neste dia</p>
                <Button
                  size="sm"
                  className="neo-btn mt-3"
                  onClick={() => {
                    setDayModalOpen(false);
                    setSelectedTask(null);
                    setFormData(prev => ({
                      ...prev,
                      title: '', description: '', category: 'geral', tags: [], status: 'pending',
                      due_date: selectedDay.date, due_time: '',
                      is_recurring: false, recurrence_type: null,
                      is_financial: false, financial_type: null, financial_amount: null,
                    }));
                    setModalOpen(true);
                  }}
                  data-testid="day-modal-create-task"
                >
                  <Plus size={16} className="mr-1" /> Criar tarefa
                </Button>
              </div>
            ) : (
              <>
                {selectedDay.tasks.map(task => {
                  const cat = getCategoryConfig(task.category);
                  const isDone = task.status === 'completed';
                  const isInProgress = task.status === 'in_progress';
                  const statusCol = COLUMNS.find(c => c.id === task.status);

                  return (
                    <div
                      key={task.task_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all
                        ${isDone ? 'bg-green-50/50 border-green-200' : isInProgress ? 'bg-blue-50/50 border-blue-200' : 'border-gray-200 hover:bg-gray-50'}`}
                      data-testid={`day-modal-task-${task.task_id}`}
                    >
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${BACKEND_URL}/api/tasks/${task.task_id}/toggle-status`, {}, { withCredentials: true });
                            toast.success('Status atualizado!');
                            fetchTasks();
                            fetchStats();
                            // Update tasks in the day modal
                            const updatedTasks = selectedDay.tasks.map(t =>
                              t.task_id === task.task_id
                                ? { ...t, status: t.status === 'pending' ? 'in_progress' : t.status === 'in_progress' ? 'completed' : 'pending' }
                                : t
                            );
                            setSelectedDay(prev => ({ ...prev, tasks: updatedTasks }));
                          } catch { toast.error('Erro ao atualizar'); }
                        }}
                        className="shrink-0"
                        data-testid={`day-modal-toggle-${task.task_id}`}
                      >
                        {statusCol && <statusCol.icon size={20} weight="bold" className={statusCol.color} />}
                      </button>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${isDone ? 'line-through text-gray-400' : ''}`}>{task.title}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold text-white ${cat.color}`}>{cat.label}</span>
                          {task.due_time && <span className="text-[10px] text-[var(--neo-text-light)]">{task.due_time}</span>}
                        </div>
                      </div>

                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => { setDayModalOpen(false); openEditModal(task); }}
                          className="p-1.5 rounded hover:bg-[var(--neo-bg-dark)]"
                          data-testid={`day-modal-edit-${task.task_id}`}
                        >
                          <PencilSimple size={14} className="text-[var(--neo-text-light)]" />
                        </button>
                        <button
                          onClick={() => { handleDelete(task.task_id); setDayModalOpen(false); }}
                          className="p-1.5 rounded hover:bg-red-100"
                          data-testid={`day-modal-delete-${task.task_id}`}
                        >
                          <Trash size={14} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => {
                    setDayModalOpen(false);
                    setSelectedTask(null);
                    setFormData(prev => ({
                      ...prev,
                      title: '', description: '', category: 'geral', tags: [], status: 'pending',
                      due_date: selectedDay.date, due_time: '',
                      is_recurring: false, recurrence_type: null,
                      is_financial: false, financial_type: null, financial_amount: null,
                    }));
                    setModalOpen(true);
                  }}
                  data-testid="day-modal-add-task"
                >
                  <Plus size={16} className="mr-1" /> Adicionar tarefa neste dia
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== CREATE/EDIT MODAL ========== */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" aria-describedby="task-form-desc">
          <DialogHeader>
            <DialogTitle>{selectedTask ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle>
            <p id="task-form-desc" className="text-sm text-muted-foreground sr-only">Formulário para criar ou editar uma tarefa</p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Título *</label>
              <Input
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Reunião com cliente"
                required
                data-testid="task-form-title"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Descrição</label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detalhes..."
                rows={2}
                data-testid="task-form-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Categoria</label>
                <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="task-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Status</label>
                <Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="task-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="completed">Concluída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Data</label>
                <Input type="date" value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })} data-testid="task-form-date" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Horário</label>
                <Input type="time" value={formData.due_time} onChange={e => setFormData({ ...formData, due_time: e.target.value })} data-testid="task-form-time" />
              </div>
            </div>

            {/* Recurring task toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--neo-bg)]">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={e => setFormData({ ...formData, is_recurring: e.target.checked })}
                className="w-4 h-4 rounded"
                data-testid="task-form-recurring"
              />
              <label htmlFor="is_recurring" className="text-sm font-medium flex items-center gap-1">
                <ArrowsClockwise size={16} className="text-purple-500" />
                Tarefa recorrente
              </label>
              {formData.is_recurring && (
                <Select value={formData.recurrence_type || 'daily'} onValueChange={v => setFormData({ ...formData, recurrence_type: v })}>
                  <SelectTrigger className="w-32" data-testid="task-form-recurrence-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diária</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensal</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Financial link toggle */}
            <div className="space-y-2 p-3 rounded-lg bg-[var(--neo-bg)]">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_financial"
                  checked={formData.is_financial}
                  onChange={e => setFormData({ ...formData, is_financial: e.target.checked })}
                  className="w-4 h-4 rounded"
                  data-testid="task-form-financial"
                />
                <label htmlFor="is_financial" className="text-sm font-medium flex items-center gap-1">
                  <CurrencyDollar size={16} className="text-green-500" />
                  Vincular a transação financeira
                </label>
              </div>
              {formData.is_financial && (
                <div className="grid grid-cols-2 gap-3 mt-2 pl-7">
                  <Select value={formData.financial_type || 'expense'} onValueChange={v => setFormData({ ...formData, financial_type: v })}>
                    <SelectTrigger data-testid="task-form-financial-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Despesa</SelectItem>
                      <SelectItem value="income">Receita</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Valor R$"
                    value={formData.financial_amount || ''}
                    onChange={e => setFormData({ ...formData, financial_amount: parseFloat(e.target.value) || null })}
                    data-testid="task-form-financial-amount"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Tags</label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  placeholder="Adicionar tag..."
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); }}}
                  data-testid="task-form-tag-input"
                />
                <Button type="button" variant="outline" onClick={addTag}><Plus size={16} /></Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="pr-1">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-red-500"><X size={12} /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" className="neo-btn" data-testid="task-form-submit">
                {selectedTask ? 'Salvar' : 'Criar Tarefa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
